import React, { useState } from "react";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { AppSidebar } from "@/components/dashboard/SimpleSidebar";
import { Menu, X } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import { getSubscriptionState } from "@/utils/billing";

interface DashboardLayoutProps {
  activePath: string;
  children: React.ReactNode;
  title?: string;
  noindex?: boolean;
  nofollow?: boolean;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
  }>;
}

export default function DashboardLayout({
  activePath,
  children,
  title,
  noindex = true,
  nofollow = true,
  breadcrumbs,
}: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { account } = useCurrentAccount();
  const subscriptionState = getSubscriptionState(account);

  return (
    <>
      {title && <Head>{generateNextSeo({ title, noindex, nofollow })}</Head>}
      <div className="flex min-h-screen w-full">
        {/* Desktop Sidebar */}
        <AppSidebar activePath={activePath} />

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <AppSidebar
              activePath={activePath}
              className="fixed left-0 top-0 bottom-0 w-64 flex bg-sidebar-background"
            />
          </div>
        )}

        <div className="flex-1 flex flex-col">
          {subscriptionState.status === "expired" && (
            <div className="bg-destructive text-destructive-foreground px-4 py-2 text-sm text-center">
              Your trial has expired. Your monitors are paused.{" "}
              <Link href="/dashboard/billing" className="underline font-medium">
                Subscribe now
              </Link>{" "}
              to resume monitoring.
            </div>
          )}
          {subscriptionState.status === "trialing" && !account?.current_payment_method && (
            <div className="bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 px-4 py-2 text-sm text-center">
              Your free trial ends in {subscriptionState.daysRemaining} day
              {subscriptionState.daysRemaining !== 1 ? "s" : ""}.{" "}
              <Link href="/dashboard/billing" className="underline font-medium">
                Subscribe now
              </Link>{" "}
              to keep your monitors running.
            </div>
          )}
          {subscriptionState.status === "past_due" && (
            <div className="bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 px-4 py-2 text-sm text-center">
              Your last payment failed. Please{" "}
              {account?.pending_invoice_url ? (
                <a
                  href={account.pending_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium cursor-pointer"
                >
                  complete the payment
                </a>
              ) : (
                <Link href="/dashboard/billing" className="underline font-medium">
                  update your payment method
                </Link>
              )}{" "}
              to avoid alerts being paused.
            </div>
          )}
          {subscriptionState.status === "unpaid" && (
            <div className="bg-destructive text-destructive-foreground px-4 py-2 text-sm text-center">
              Your subscription is unpaid. Alert notifications are paused.{" "}
              {account?.pending_invoice_url ? (
                <a
                  href={account.pending_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium cursor-pointer"
                >
                  Complete the payment
                </a>
              ) : (
                <Link href="/dashboard/billing" className="underline font-medium">
                  Update your payment method
                </Link>
              )}{" "}
              to resume monitoring.
            </div>
          )}
          <header className="flex h-16 items-center gap-2 border-b bg-background px-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link href="/dashboard">Dashboard</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        {crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link href={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </header>
          <main className="flex-1">{children}</main>
        </div>
      </div>
    </>
  );
}
